import {createSlice, isAnyOf, type SliceSelectors} from '@reduxjs/toolkit'

import {isEmpty, isNil} from '@shared/helpers/lang'

import {
    selectCardPromotionGroup,
    clearSelectedCardPromotion,
    checkCardPromotionPolicy,
} from '@web/actions/cardPromotion'
import {
    getCardEasyPays,
    selectCardEasyPayPayMethod,
    selectCardPayMethod,
    selectComplexPayMethod,
    selectSinglePaymentMethod,
} from '@web/actions/paymentMethod'
import {applySimulateCouponPage, cancelSelectCoupon, selectProductCoupon} from '@web/actions/productCoupon'
import {restoreSavedState, restoreState} from '@web/actions/restoreState'
import {
    EasyCardItemType,
    NonInstallment,
    PaymentsCardCode,
    PaymentMethodEntry,
    PayMethod,
    PromotionStatus,
    PromotionValidationSkipType,
} from '@web/core/constants'
import {
    cancelConnectingPromotionAccount,
    toggleUseAppCardDirectCall,
    changePointUseOfSelectedEasyCardCorp,
    resetCreditCardInstallment,
    resetEasyCardInstallment,
    selectCreditCard,
    selectCreditCardCode,
    selectCreditCardInstallment,
    selectEasyCard,
    selectEasyCardInstallment,
    selectWelfareCard,
    skipPromotionValidation,
    getInitialState,
    promotionGroupItemConverter,
    promotionUtil,
    updateSamsungPayMoney,
} from '@web/slices/card/slice'

import type {CardReducers, MakeSliceOptions} from './interfaces'
import type {CardState, CardView} from '@web/slices/card/slice'

function getPromotionCardItem({
    state,
    cardCompanyCode,
    cardProductNameCode,
}: {
    state: CardState
    cardCompanyCode: string
    cardProductNameCode: Maybe<string>
}) {
    const promotionGroups = state.promotion.groups.filter((group) => !isEmpty(group.cardPromotions))
    const selectedIndex = cardProductNameCode
        ? promotionGroups.findIndex((group) => group.card.productNameCode === cardProductNameCode)
        : promotionGroups.findIndex(
              (group) => group.card.companyCode === cardCompanyCode && isNil(group.card.productNameCode),
          )

    const selectedGroupInfo = promotionGroups[selectedIndex]
        ? promotionGroupItemConverter(promotionGroups[selectedIndex])
        : null

    if (!selectedGroupInfo) {
        return []
    }

    return state.cardEasyPay.items.filter((cardItem2) => {
        return promotionUtil.isApplicableEasyCard({
            targetCard: {
                companyCode: cardItem2.companyCode,
                type: cardItem2.type,
                ownerType: cardItem2.ownerType,
                issuerCode: cardItem2.issuerCode,
                companyId: cardItem2.companyId,
                productCode: cardItem2.productCode,
            },
            promotionCardInfo: {
                companyCode: selectedGroupInfo.card.companyCode,
                companyName: selectedGroupInfo.card.companyName,
                detailInfos: selectedGroupInfo.card.detailInfos.map(
                    ({companyId, issuerCode, types, ownerTypes, productCodes}) => {
                        return {
                            companyId,
                            issuerCode,
                            types,
                            ownerTypes,
                            productCodes,
                        }
                    },
                ),
                productNameCode: selectedGroupInfo.card.productNameCode,
                productName: selectedGroupInfo.card.productName,
            },
        })
    })
}

/**
 * - 카드사별 앱카드 이름
 * - @CHECK 카드사별 앱카드 추가시 key값 변경 및 업데이트
 */
export const appCardNameMap: Record<string, string> = {
    [PaymentsCardCode.LOTTE]: '로카페이',
}

/**
 * cardItem 목록중 첫번째 카드를 선택해줍니다.
 * - 만약 카드 목록이 없다면, 배너 카드 혹은 추가 카드를 선택합니다.
 * - 프로모션이 선택되어 있는경우에는, 배너타입이 'PROMOTION_PLATE_BANNER'인 경우에만 유효한 배너입니다.
 *   - 선택된 프로모션이 카드사 프로모션인 경우이거나, 카드상품 프로모션이라면 cardProductNameCode가 일치해야합니다.
 */
function updateSelectedEasyCardByFirst({
    state,
    cardItems,
    bannerItemType,
}: {
    state: CardState
    cardItems: CardView[]
    bannerItemType: Maybe<{
        cardCompanyCode: string
        bannerType: string
        cardProductNameCode: Maybe<string>
    }>
}) {
    const item = cardItems.find(({payService}) => !!payService)
    const isExistSelectedPromotion = !!state.promotion.selectedInfo

    state.cardEasyPay.selected.id = item?.id ?? ''
    state.cardEasyPay.selected.itemType = !item
        ? state.cardEasyPay.bannerItemTypes.some(
              ({cardCompanyCode, bannerType, cardProductNameCode}) =>
                  cardCompanyCode === bannerItemType?.cardCompanyCode &&
                  (isExistSelectedPromotion
                      ? bannerType === 'PROMOTION_PLATE_BANNER' &&
                        (!state.promotion.selectedInfo?.cardProductNameCode ||
                            cardProductNameCode === bannerItemType?.cardProductNameCode)
                      : bannerItemType?.bannerType === bannerType),
          )
            ? EasyCardItemType.ADD_CARD
            : EasyCardItemType.ADD_CARD
        : EasyCardItemType.CARD
    state.cardEasyPay.selected.installment = NonInstallment
}

export default (options?: MakeSliceOptions) =>
    createSlice<CardState, CardReducers, 'card', SliceSelectors<CardState>>({
        name: 'card',
        initialState: getInitialState(),
        reducers: {},
        extraReducers: (builder) => {
            builder
                .addCase(selectEasyCard, (state, {payload}) => {
                    state.cardEasyPay.selected.id = payload.id
                    state.cardEasyPay.selected.installment = payload.installment ?? NonInstallment
                    state.cardEasyPay.selected.itemType = payload.itemType ?? EasyCardItemType.CARD
                    state.cardEasyPay.selected.usingCardCorpPoint = false
                })
                .addCase(selectEasyCardInstallment, (state, {payload}) => {
                    state.cardEasyPay.selected.installment = payload
                })
                .addCase(resetEasyCardInstallment, (state) => {
                    state.cardEasyPay.selected.installment = NonInstallment
                })
                .addCase(changePointUseOfSelectedEasyCardCorp, (state, {payload}) => {
                    if (state.cardEasyPay.selected) {
                        state.cardEasyPay.selected.usingCardCorpPoint = payload
                    }
                })
                .addCase(selectCreditCard, (state, {payload}) => {
                    state.creditCard.selected.type = payload.type
                    state.creditCard.selected.companyCode = payload.companyCode ?? null
                    state.creditCard.selected.installment = payload.installment ?? NonInstallment
                    state.creditCard.selected.appCardInfo = null
                })
                .addCase(selectCreditCardCode, (state, {payload: {companyCode, installment, canUseAppCard}}) => {
                    state.creditCard.selected.companyCode = companyCode
                    state.creditCard.selected.installment = installment ?? NonInstallment
                    state.creditCard.selected.appCardInfo =
                        canUseAppCard && companyCode && appCardNameMap[companyCode]
                            ? {
                                  name: appCardNameMap[companyCode],
                                  useDirectCall: true,
                              }
                            : null
                })
                .addCase(toggleUseAppCardDirectCall, (state) => {
                    if (state.creditCard.selected.appCardInfo) {
                        state.creditCard.selected.appCardInfo.useDirectCall =
                            !state.creditCard.selected.appCardInfo.useDirectCall
                    }
                })
                .addCase(selectCreditCardInstallment, (state, {payload}) => {
                    state.creditCard.selected.installment = payload
                })
                .addCase(resetCreditCardInstallment, (state) => {
                    state.creditCard.selected.installment = NonInstallment
                })
                .addCase(selectWelfareCard, (state, {payload}) => {
                    state.welfareCard.selected.companyCode = payload.companyCode ? payload.companyCode : null
                })
                .addCase(clearSelectedCardPromotion, (state) => {
                    updateSelectedEasyCardByFirst({
                        state,
                        cardItems: state.cardEasyPay.items,
                        // 카드가 없을때 매칭되어야하는 배너가 있을 경우 추가
                        bannerItemType: null,
                    })

                    state.promotion.selectedInfo = null
                    state.promotion.status = state.promotion.myOptimalDiscountGroup
                        ? PromotionStatus.HAS_MY_OPTIMIZED_PROMOTION
                        : PromotionStatus.AVAILABLE

                    state.promotion.skipInfo.isConnectedEasyBank = false
                })
                .addCase(skipPromotionValidation, (state, {payload: {skipType}}) => {
                    if (skipType === PromotionValidationSkipType.SUGGEST_PROMOTION) {
                        state.promotion.skipInfo.isSuggested = true
                    }
                    if (skipType === PromotionValidationSkipType.CONNECT_BANK) {
                        state.promotion.skipInfo.isSuggested = true
                        state.promotion.skipInfo.isConnectedEasyBank = true
                    }
                })
                .addCase(cancelConnectingPromotionAccount, (state) => {
                    state.promotion.status = PromotionStatus.AVAILABLE
                })
                .addCase(getCardEasyPays.fulfilled, (state, {payload}) => {
                    state.cardEasyPay.items = payload.cardEasyPays
                    /**
                     * 업데이트된 간편카드 목록에 선택된 카드가 없는경우 or 처음 카드가 추가된 경우,
                     * 간편카드 목록의 첫번째 카드를 선택된 상태로 초기화합니다.
                     */
                    updateSelectedEasyCardByFirst({
                        state,
                        cardItems: payload.cardEasyPays,
                        // 카드가 없을때 매칭되어야하는 배너가 있을 경우 추가
                        bannerItemType: null,
                    })

                    /**
                     * 간편 카드 추가시
                     * - 선택된 프로모션이 있다면, 0번째 카드와 selected의 companyCode 비교하여 다른경우 프로모션 그룹 초기화 필요
                     */
                    const promotionGroups = state.promotion.groups
                    const selectedPromotion = state.promotion.selectedInfo
                        ? (() => {
                              const rawSelectedPromotion = promotionGroups.find(
                                  (group) => group.card.companyCode === state.promotion.selectedInfo?.cardCompanyCode,
                              )
                              return rawSelectedPromotion ? promotionGroupItemConverter(rawSelectedPromotion) : null
                          })()
                        : null

                    const newCard = payload.cardEasyPays[0]

                    if (
                        !!selectedPromotion &&
                        newCard &&
                        !promotionUtil.isApplicableEasyCard({
                            targetCard: {
                                companyCode: newCard.companyCode,
                                type: newCard.type,
                                ownerType: newCard.ownerType,
                                issuerCode: newCard.issuerCode,
                                companyId: newCard.companyId,
                                productCode: newCard.productCode,
                            },
                            promotionCardInfo: selectedPromotion.card,
                        })
                    ) {
                        state.promotion.status = PromotionStatus.SHOULD_INIT
                    }
                })
                .addCase(
                    selectCardPromotionGroup.fulfilled,
                    (state, {payload: {cardCompanyCode, hasNoAccounts, cardProductNameCode, isMyDiscount}}) => {
                        if (isMyDiscount && state.promotion.myOptimizedGroups) {
                            state.promotion.groups = state.promotion.myOptimizedGroups
                        }

                        state.promotion.selectedInfo = {
                            cardCompanyCode,
                            cardProductNameCode,
                        }

                        updateSelectedEasyCardByFirst({
                            state,
                            cardItems: getPromotionCardItem({state, cardCompanyCode, cardProductNameCode}),
                            bannerItemType: {
                                cardCompanyCode,
                                bannerType: cardProductNameCode ? 'PROMOTION_PLATE_BANNER' : 'PLATE_BANNER',
                                cardProductNameCode,
                            },
                        })

                        /** 비번창까지 갔다가, 다시 돌아와서 프로모션 선택하는 경우 validation다시 필요하므로 skip 데이터 처리 */
                        state.promotion.skipInfo.isConnectedEasyBank = false

                        state.promotion.skipInfo.isSuggested = false

                        if (hasNoAccounts) {
                            state.promotion.status = PromotionStatus.CHECK_CONNECTING_EASY_BANK
                        }
                    },
                )
                .addCase(selectSinglePaymentMethod.fulfilled, (state, {payload}) => {
                    if (payload.selectedPayMethod === PayMethod.CARD_EASY_PAY) {
                        state.cardEasyPay.selected.id = payload.id ?? state.cardEasyPay.selected.id
                        state.cardEasyPay.selected.installment = payload.installment
                        state.cardEasyPay.selected.itemType = EasyCardItemType.CARD
                        state.cardEasyPay.selected.usingCardCorpPoint = false
                    }
                })

                .addCase(cancelSelectCoupon, (state) => {
                    if (state.promotion.status === PromotionStatus.NO_PROMOTION) {
                        return
                    }

                    state.promotion.status = PromotionStatus.AVAILABLE
                })
            builder
                .addCase(selectCardEasyPayPayMethod.fulfilled, (state, {payload}) => {
                    if (payload.entry === PaymentMethodEntry.INITIALIZE) {
                        updateSelectedEasyCardByFirst({
                            state,
                            cardItems: state.cardEasyPay.items,
                            // 카드가 없을때 매칭되어야하는 배너가 있을 경우 추가
                            bannerItemType: null,
                        })
                    } else {
                        state.cardEasyPay.selected.id = payload.id ?? null
                        state.cardEasyPay.selected.installment = payload.installment ?? NonInstallment
                        state.cardEasyPay.selected.itemType = payload.itemType ?? EasyCardItemType.CARD
                        state.cardEasyPay.selected.usingCardCorpPoint = false
                    }
                })
                .addCase(selectCardPayMethod.fulfilled, (state, {payload}) => {
                    if (payload.entry === PaymentMethodEntry.INITIALIZE) {
                        // 카드 선택 확인 필요
                        state.creditCard.selected.companyCode = null
                        state.creditCard.selected.installment = NonInstallment
                    } else if (payload.type) {
                        state.creditCard.selected.type = payload.type
                        state.creditCard.selected.companyCode = payload.companyCode ?? null
                        state.creditCard.selected.installment = payload.installment ?? NonInstallment
                    }
                })
                .addCase(applySimulateCouponPage, (state, {payload}) => {
                    if (state.promotion.status === PromotionStatus.NO_PROMOTION) {
                        return
                    }

                    if (payload.isSelectingChanged) {
                        state.promotion.status = PromotionStatus.SHOULD_RELOAD
                    }
                })
                .addCase(updateSamsungPayMoney, (state, {payload}) => {
                    state.creditCard.samsungPayInfo = payload ?? []
                })
            options?.extraReducers?.(builder)
            builder
                .addMatcher(isAnyOf(selectComplexPayMethod.fulfilled), (state, {payload}) => {
                    if (payload.selectedPayMethod === PayMethod.CARD_EASY_PAY) {
                        updateSelectedEasyCardByFirst({
                            state,
                            cardItems: state.cardEasyPay.items,
                            // 카드가 없을때 매칭되어야하는 배너가 있을 경우 추가
                            bannerItemType: null,
                        })
                    } else if (
                        payload.selectedPayMethod === PayMethod.CARD &&
                        payload.payMethods.includes(PayMethod.CARD)
                    ) {
                        // 카드 선택 확인 필요
                        state.creditCard.selected.companyCode = null
                        state.creditCard.selected.installment = NonInstallment
                    }
                })
                .addMatcher(isAnyOf(selectProductCoupon, checkCardPromotionPolicy), (state) => {
                    if (state.promotion.status === PromotionStatus.NO_PROMOTION) {
                        return
                    }

                    state.promotion.status = PromotionStatus.SHOULD_RELOAD
                })
                .addMatcher(isAnyOf(restoreSavedState.fulfilled, restoreState), (state, {payload}) => {
                    return payload.card
                })
        },
    })
